import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef, Input, Output, EventEmitter } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { SharedModule, FormInputComponent, StringCustomFieldConfig, NotificationService } from '@vendure/admin-ui/core';
import { Subscription } from 'rxjs';
import { safeJSONParse } from '@common-ui/utils';
import { TextFrameGroupConfig, CustomTextFramesContent, TextFrameColorOption, normalizeTextFrameColor } from '../common/ui-types';
import { PRINT_FONT_CATALOG, FontCatalogEntry } from '../common/font-catalog';
import { DEFAULT_TEXT_FRAME_COLORS } from '../common/color-defaults';

interface GroupControls {
  defaultFont: FormControl<string>;
  defaultColor: FormControl<string>;
}

@Component({
  selector: 'text-frames-form',
  template: `
    <div class="text-frames-form">
      <!-- Label & Description (standalone mode only) -->
      <div class="form-grid" *ngIf="!isEmbedded">
        <vdr-form-field label="Label" for="tf-label">
          <input id="tf-label" type="text" [formControl]="labelControl" />
        </vdr-form-field>
        <vdr-form-field label="Description" for="tf-description">
          <input id="tf-description" type="text" [formControl]="descriptionControl" />
        </vdr-form-field>
      </div>

      <!-- Groups -->
      <div class="section-header">
        <span class="section-title">Text Overlay Groups</span>
        <button class="btn btn-primary btn-sm" (click)="addTextFrameGroup()">+ Add Group</button>
      </div>

      <div *ngFor="let group of content.textFrameGroups; let gi = index; trackBy: trackByIndex" class="text-frame-group-card">
        <div class="group-header">
          <span class="group-title">Group {{ gi + 1 }}: {{ group.name }}</span>
          <button class="btn btn-danger btn-sm" (click)="removeTextFrameGroup(gi)">Remove Group</button>
        </div>

        <div class="group-body">
          <div class="form-row">
            <vdr-form-field [label]="'Group Name'" class="form-item">
              <input type="text" [value]="group.name" (input)="onGroupFieldChange(gi, 'name', $any($event.target).value)" class="form-control" />
            </vdr-form-field>
          </div>

          <!-- Allowed Fonts -->
          <div class="form-row"><span class="form-label">Allowed Fonts (uncheck all = allow all)</span></div>
          <div class="font-checkboxes">
            <label *ngFor="let font of fontCatalog" class="font-checkbox">
              <input type="checkbox"
                [checked]="group.allowedFonts.includes(font.family)"
                (change)="toggleFontInGroup(gi, font.family)" />
              <span class="font-preview-label" [style.fontFamily]="font.family">{{ font.family }}</span>
              <span class="font-category-badge">{{ font.category }}</span>
            </label>
          </div>

          <div class="form-row" *ngIf="groupControls[gi]">
            <vdr-form-field [label]="'Default Font'" class="form-item">
              <select [formControl]="groupControls[gi].defaultFont"
                class="form-control font-preview-select" [style.fontFamily]="groupControls[gi].defaultFont.value">
                <option *ngFor="let font of getEffectiveFonts(group)" [value]="font.family"
                  [style.font-family]="font.family">{{ font.family }}</option>
              </select>
            </vdr-form-field>
          </div>

          <!-- Color Palette -->
          <div class="form-row"><span class="form-label">Color Palette</span></div>
          <div class="color-palette">
            <div *ngFor="let color of group.allowedColors; let ci = index" class="color-swatch-item">
              <div class="color-swatch" [style.backgroundColor]="color.hex"
                [class.active]="color.hex === group.defaultColor"
                [title]="color.name || color.hex"
                (click)="setDefaultColor(gi, color.hex)"></div>
              <span class="color-hex-label">{{ color.hex }}</span>
              <input class="color-name-edit" [value]="color.name"
                (change)="updateColorName(gi, ci, $event)" placeholder="Name" maxlength="16" />
              <button class="btn-remove-color" (click)="removeColorFromGroup(gi, ci)">&times;</button>
            </div>
            <div class="add-color-cell">
              <button class="btn-add-color" (click)="openAddColorModal(gi)">+</button>
            </div>
          </div>
          <div *ngIf="addColorGroupIndex === gi" class="color-modal-backdrop" (click)="cancelAddColor()">
            <div class="color-modal" (click)="$event.stopPropagation()">
              <div class="color-modal-header">Add Color</div>
              <div class="color-modal-body">
                <div class="color-modal-row">
                  <label class="color-modal-label">Color</label>
                  <input type="color" [(ngModel)]="addColorHex" class="color-input-native color-input-lg" />
                  <span class="color-modal-hex">{{ addColorHex }}</span>
                </div>
                <div class="color-modal-row">
                  <label class="color-modal-label">Name</label>
                  <input type="text" [(ngModel)]="addColorName" [placeholder]="addColorHex" class="form-control color-modal-name-input" />
                </div>
              </div>
              <div class="color-modal-footer">
                <button class="btn btn-secondary btn-sm" (click)="cancelAddColor()">Cancel</button>
                <button class="btn btn-primary btn-sm" (click)="confirmAddColor()">OK</button>
              </div>
            </div>
          </div>

          <div class="form-row default-color-form-row" *ngIf="groupControls[gi]">
            <span class="form-label">Default Color</span>
            <div class="default-color-row">
              <div class="color-swatch color-swatch-sm" [style.backgroundColor]="groupControls[gi].defaultColor.value"></div>
              <select [formControl]="groupControls[gi].defaultColor" class="form-control">
                <option *ngFor="let color of group.allowedColors" [value]="color.hex">{{ color.name || color.hex }}</option>
              </select>
            </div>
          </div>

          <!-- Frames -->
          <div class="frames-section">
            <div class="frames-header">
              <span class="form-label">Text Boxes</span>
              <button class="btn btn-secondary btn-sm" (click)="addFrameToGroup(gi)">+ Add Text Box</button>
            </div>
            <div *ngFor="let frame of group.frames; let fi = index; trackBy: trackByIndex" class="frame-card">
              <div class="frame-card-header">
                <span>Text Box {{ fi + 1 }}</span>
                <button *ngIf="group.frames.length > 1" class="btn btn-danger btn-sm" (click)="removeFrameFromGroup(gi, fi)">Remove</button>
              </div>
              <div class="form-row">
                <vdr-form-field [label]="'Name'" class="form-item">
                  <input type="text" [value]="frame.name" (input)="onFrameNameChange(gi, fi, $any($event.target).value)" class="form-control" />
                </vdr-form-field>
                <vdr-form-field [label]="'Label (placeholder)'" class="form-item">
                  <input type="text" [value]="frame.label || ''" (input)="onFrameFieldChange(gi, fi, 'label', $any($event.target).value)" class="form-control" />
                </vdr-form-field>
              </div>
              <div class="form-row">
                <vdr-form-field [label]="'Required'" class="form-item-toggle">
                  <clr-toggle-wrapper>
                    <input type="checkbox" clrToggle
                      [checked]="frame.required"
                      (change)="onFrameFieldChange(gi, fi, 'required', $any($event.target).checked)" />
                  </clr-toggle-wrapper>
                </vdr-form-field>
                <vdr-form-field [label]="'Max Length (characters)'" class="form-item">
                  <input type="number" [value]="frame.maxLength" (input)="onFrameFieldChange(gi, fi, 'maxLength', +$any($event.target).value)" class="form-control" />
                </vdr-form-field>
                <vdr-form-field [label]="'Max Lines'" class="form-item">
                  <input type="number" [value]="frame.maxLines ?? 3" min="1" (input)="onFrameFieldChange(gi, fi, 'maxLines', +$any($event.target).value || 3)" class="form-control" />
                </vdr-form-field>
                <vdr-form-field [label]="'Text Align'" *ngIf="frameAlignControls[gi]?.[fi]" class="form-item">
                  <select [formControl]="frameAlignControls[gi][fi]" class="form-control">
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </vdr-form-field>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .text-frames-form { max-width: 820px; }
    .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; margin-top: 16px; }
    .section-title { font-size: 16px; font-weight: bold; }
    .text-frame-group-card { border: 1px solid #d1d5db; border-radius: 6px; margin-bottom: 16px; background: #fafafa; }
    .group-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; background: #f3f4f6; border-bottom: 1px solid #d1d5db; border-radius: 6px 6px 0 0; }
    .group-title { font-weight: 600; }
    .group-body { padding: 14px; }
    .font-checkboxes { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
    .font-checkbox { display: flex; align-items: center; gap: 4px; font-size: 13px; cursor: pointer; }
    .color-palette { display: grid; grid-template-columns: repeat(auto-fill, minmax(88px, 1fr)); gap: 8px; margin-bottom: 12px; }
    .color-swatch-item { display: flex; flex-direction: column; align-items: center; gap: 2px; }
    .color-swatch { width: 28px; height: 28px; border-radius: 50%; border: 2px solid #ccc; cursor: pointer; }
    .color-swatch.active { border-color: #333; box-shadow: 0 0 0 2px #666; }
    .btn-remove-color { background: none; border: none; font-size: 14px; color: #999; cursor: pointer; padding: 0; line-height: 1; }
    .btn-remove-color:hover { color: #e00; }
    .color-hex-label { font-size: 10px; color: #888; font-family: monospace; text-align: center; }
    .color-name-edit { width: 88px; height: 20px; font-size: 11px; padding: 0 3px; border: 1px solid #ddd; border-radius: 3px; text-align: center; }
    .add-color-cell { display: flex; align-items: center; justify-content: center; min-height: 88px; }
    .btn-add-color { width: 40px; height: 40px; border-radius: 50%; border: 2px dashed #aaa; background: #fff; font-size: 24px; line-height: 1; color: #888; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; }
    .btn-add-color:hover { border-color: #333; color: #333; background: #f3f4f6; }
    .color-input-native { border: 2px solid #ccc; border-radius: 4px; padding: 0; cursor: pointer; -webkit-appearance: none; appearance: none; background: none; overflow: hidden; }
    .color-input-native::-webkit-color-swatch-wrapper { padding: 0; }
    .color-input-native::-webkit-color-swatch { border: none; border-radius: 2px; }
    .color-input-native::-moz-color-swatch { border: none; border-radius: 2px; }
    .color-input-lg { width: 48px; height: 36px; min-width: 48px; }
    .color-modal-backdrop { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.3); z-index: 1000; display: flex; align-items: center; justify-content: center; }
    .color-modal { background: #fff; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.2); width: 320px; overflow: hidden; }
    .color-modal-header { padding: 12px 16px; font-weight: 600; font-size: 14px; border-bottom: 1px solid #e5e7eb; }
    .color-modal-body { padding: 16px; display: flex; flex-direction: column; gap: 12px; }
    .color-modal-row { display: flex; align-items: center; gap: 10px; }
    .color-modal-label { font-size: 13px; font-weight: 500; min-width: 44px; }
    .color-modal-hex { font-size: 13px; color: #666; font-family: monospace; }
    .color-modal-name-input { flex: 1; height: 32px; font-size: 13px; }
    .color-modal-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 16px; border-top: 1px solid #e5e7eb; }
    .default-color-form-row { flex-direction: column; gap: 4px; }
    .default-color-row { display: flex; align-items: center; gap: 8px; }
    .color-swatch-sm { width: 22px; height: 22px; min-width: 22px; flex-shrink: 0; }
    .form-row { display: flex; gap: 12px; margin-bottom: 8px; }
    .form-item { flex: 1; }
    .form-item-toggle { flex: 0 0 auto; }
    .form-label { font-weight: 600; font-size: 13px; }
    .frames-section { margin-top: 16px; }
    .frames-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
    .frame-card { border: 1px solid #e5e7eb; border-radius: 4px; padding: 10px; margin-bottom: 8px; background: #fff; }
    .frame-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; font-weight: 500; }
    .font-preview-label { font-size: 15px; }
    .font-category-badge { font-size: 10px; background: #e5e7eb; color: #555; padding: 1px 6px; border-radius: 8px; margin-left: 2px; }
    .font-preview-select { font-size: 15px; }
  `],
  standalone: true,
  imports: [SharedModule, FormsModule],
})
export class TextFramesFormComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, OnChanges, OnDestroy {
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  /**
   * Embedded-mode input. When bound, the component treats this array as the
   * source of truth and emits `groupsChanged` instead of writing to
   * `formControl`. Label/description UI is hidden in this mode — it only applies
   * to the standalone `custom-text-frames` option type.
   */
  @Input() groups: TextFrameGroupConfig[] | null = null;
  @Output() groupsChanged = new EventEmitter<TextFrameGroupConfig[]>();

  /**
   * Owning image-frame name, supplied in embedded (nested) mode by the pattern
   * editor. When set, a newly-added group defaults to "{frame} Text Overlay"
   * instead of the generic "Text Group N", so the stored name is meaningful at
   * the source (matches the storefront's display/attribution).
   */
  @Input() groupNameContext?: string;

  /**
   * True when the component is used as a sub-form (e.g. inside the pattern-editor
   * per-frame panel) — set from `ngOnChanges` whenever `[groups]` is bound.
   * In embedded mode we skip `formControl` reads/writes entirely.
   */
  isEmbedded = false;

  labelControl = new FormControl('');
  descriptionControl = new FormControl('');

  groupControls: GroupControls[] = [];
  frameAlignControls: FormControl<string>[][] = [];

  fontCatalog: FontCatalogEntry[] = PRINT_FONT_CATALOG;
  content: CustomTextFramesContent = { label: '', description: '', textFrameGroups: [] };
  addColorGroupIndex = -1;
  addColorHex = '#000000';
  addColorName = '';

  private subs: Subscription[] = [];
  private initialised = false;

  constructor(private notificationService: NotificationService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (this.isEmbedded) {
      // Embedded mode — `ngOnChanges` already populated this.content.textFrameGroups
      // from the `[groups]` input. No formControl reads.
      this.rebuildGroupControls();
      this.loadGoogleFonts();
      this.initialised = true;
      return;
    }

    if (this.formControl?.value) {
      const raw = this.formControl.value;
      const parsed = typeof raw === 'string'
        ? safeJSONParse<CustomTextFramesContent>(raw, this.notificationService)
        : JSON.parse(JSON.stringify(raw)) as CustomTextFramesContent;
      if (parsed) {
        this.content = parsed;
      }
    }
    // Sanitize malformed imported JSON so the form can open and be repaired:
    // a non-array textFrameGroups / null entries / non-array allowedColors must
    // not throw when iterating or mapping here.
    this.content.textFrameGroups = (Array.isArray(this.content.textFrameGroups)
      ? this.content.textFrameGroups.filter(g => g != null)
      : []);
    for (const group of this.content.textFrameGroups) {
      // Coerce every list field — rebuildGroupControls() iterates group.frames
      // and the template reads group.allowedFonts, so non-array values crash.
      group.allowedFonts = (Array.isArray(group.allowedFonts) ? group.allowedFonts : []).filter(f => f != null);
      group.allowedColors = (Array.isArray(group.allowedColors) ? group.allowedColors : [])
        .filter(c => c != null)
        .map(normalizeTextFrameColor);
      group.fontSizeOptions = (Array.isArray(group.fontSizeOptions) ? group.fontSizeOptions : []).filter(s => s != null);
      group.frames = (Array.isArray(group.frames) ? group.frames : []).filter(f => f != null);
    }
    this.labelControl.setValue(this.content.label, { emitEvent: false });
    this.descriptionControl.setValue(this.content.description, { emitEvent: false });

    this.subs.push(
      this.labelControl.valueChanges.subscribe(v => { this.content.label = v ?? ''; this.updateFormValue(); }),
      this.descriptionControl.valueChanges.subscribe(v => { this.content.description = v ?? ''; this.updateFormValue(); }),
    );

    this.rebuildGroupControls();
    this.loadGoogleFonts();
    this.initialised = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('groups' in changes) {
      // Any `[groups]` binding (even initial null/[]) marks the component embedded.
      this.isEmbedded = true;
      const incoming = changes['groups'].currentValue as TextFrameGroupConfig[] | null;
      // Deep copy to avoid the parent and child mutating the same array references.
      // Guard against malformed imported JSON (e.g. textFrameGroups: {} or [null],
      // allowedColors: 'bad') so selecting the frame doesn't crash the editor —
      // mirrors the shop-api hydration sanitization in common/utils.ts.
      const incomingGroups = Array.isArray(incoming) ? incoming.filter(g => g != null) : [];
      const normalised = incomingGroups.map(g => ({
        ...g,
        // Coerce every list field to a clean array: rebuildGroupControls()
        // iterates group.frames and the template reads group.allowedFonts, so a
        // non-array value from a malformed import would crash the editor.
        allowedFonts: (Array.isArray(g.allowedFonts) ? g.allowedFonts : []).filter(f => f != null),
        allowedColors: (Array.isArray(g.allowedColors) ? g.allowedColors : [])
          .filter(c => c != null)
          .map(normalizeTextFrameColor),
        fontSizeOptions: (Array.isArray(g.fontSizeOptions) ? g.fontSizeOptions : []).filter(s => s != null),
        frames: (Array.isArray(g.frames) ? g.frames : []).filter(f => f != null),
      }));
      this.content = {
        label: this.content?.label ?? '',
        description: this.content?.description ?? '',
        textFrameGroups: normalised,
      };
      if (this.initialised) {
        // Re-hosting groups after init — rebuild dependent FormControls.
        this.rebuildGroupControls();
        this.cdr.detectChanges();
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  private loadGoogleFonts(): void {
    const id = 'text-frames-google-fonts';
    if (document.getElementById(id)) return;
    const families = this.fontCatalog.map(f => 'family=' + f.family.replace(/ /g, '+')).join('&');
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    document.head.appendChild(link);
  }

  private rebuildGroupControls(): void {
    this.subs.forEach(s => s.unsubscribe());
    // In embedded mode the label/description controls are hidden, so no need to
    // re-wire them. Standalone mode keeps the original behaviour.
    this.subs = this.isEmbedded ? [] : [
      this.labelControl.valueChanges.subscribe(v => { this.content.label = v ?? ''; this.updateFormValue(); }),
      this.descriptionControl.valueChanges.subscribe(v => { this.content.description = v ?? ''; this.updateFormValue(); }),
    ];

    this.groupControls = [];
    this.frameAlignControls = [];

    for (let gi = 0; gi < this.content.textFrameGroups.length; gi++) {
      const group = this.content.textFrameGroups[gi];
      const fontCtrl = new FormControl<string>(group.defaultFont, { nonNullable: true });
      const colorCtrl = new FormControl<string>(group.defaultColor, { nonNullable: true });

      this.subs.push(
        fontCtrl.valueChanges.subscribe(v => { group.defaultFont = v; this.updateFormValue(); }),
        colorCtrl.valueChanges.subscribe(v => { group.defaultColor = v; this.updateFormValue(); }),
      );
      this.groupControls.push({ defaultFont: fontCtrl, defaultColor: colorCtrl });

      const frameCtrls: FormControl<string>[] = [];
      for (const frame of group.frames) {
        const alignCtrl = new FormControl<string>(frame.textAlign, { nonNullable: true });
        this.subs.push(alignCtrl.valueChanges.subscribe(v => { frame.textAlign = v as any; this.updateFormValue(); }));
        frameCtrls.push(alignCtrl);
      }
      this.frameAlignControls.push(frameCtrls);
    }
  }

  updateFormValue(): void {
    if (this.isEmbedded) {
      // Embedded mode: parent owns persistence. Emit the current groups array
      // (a shallow copy so the parent can detect ref change if it uses OnPush).
      this.groupsChanged.emit(this.content.textFrameGroups.slice());
      this.cdr.detectChanges();
      return;
    }
    this.formControl.setValue(this.content);
    this.formControl.markAsDirty();
    this.cdr.detectChanges();
  }

  trackByIndex(index: number): number {
    return index;
  }

  // --- Group-level field changes ---

  onGroupFieldChange(gi: number, field: string, value: any): void {
    const group = this.content.textFrameGroups[gi];
    if (!group) return;
    (group as any)[field] = value;
    this.updateFormValue();
  }

  setDefaultColor(gi: number, color: string): void {
    const ctrl = this.groupControls[gi]?.defaultColor;
    if (ctrl) ctrl.setValue(color);
  }

  // --- Frame-level field changes ---

  onFrameNameChange(gi: number, fi: number, value: string): void {
    const frame = this.content.textFrameGroups[gi]?.frames[fi];
    if (!frame) return;
    frame.name = value;
    this.updateFormValue();
  }

  onFrameFieldChange(gi: number, fi: number, field: string, value: any): void {
    const frame = this.content.textFrameGroups[gi]?.frames[fi];
    if (!frame) return;
    (frame as any)[field] = value;
    this.updateFormValue();
  }

  // --- Group CRUD ---

  addTextFrameGroup(): void {
    const idx = this.content.textFrameGroups.length;
    // Nested (embedded) groups default to "{Frame Name} Text Overlay" — frame-aware
    // and consistent with the storefront. The first is unnumbered; extras get 2/3/…
    // Standalone groups keep the generic "Text Group N".
    const name = this.isEmbedded && this.groupNameContext?.trim()
      ? `${this.groupNameContext.trim()} Text Overlay${idx === 0 ? '' : ` ${idx + 1}`}`
      : `Text Group ${idx + 1}`;
    this.content.textFrameGroups.push({
      name,
      allowedFonts: [],
      allowedColors: DEFAULT_TEXT_FRAME_COLORS.map(c => ({ ...c })),
      defaultFont: 'Roboto',
      defaultColor: '#000000',
      autoFit: true,
      fontSizeOptions: [12, 16, 20, 24, 32],
      defaultFontSize: 16,
      frames: [{ name: 'Your Text 1', label: '', maxLength: 200, maxLines: 3, required: false, textAlign: 'center' }],
    });
    this.rebuildGroupControls();
    this.updateFormValue();
  }

  removeTextFrameGroup(gi: number): void {
    if (gi >= 0 && gi < this.content.textFrameGroups.length) {
      this.content.textFrameGroups.splice(gi, 1);
      this.rebuildGroupControls();
      this.updateFormValue();
    }
  }

  // --- Frame CRUD ---

  addFrameToGroup(gi: number): void {
    const group = this.content.textFrameGroups[gi];
    if (!group) return;
    const frameName = `Your Text ${group.frames.length + 1}`;
    group.frames.push({ name: frameName, label: '', maxLength: 200, maxLines: 3, required: false, textAlign: 'center' });
    this.rebuildGroupControls();
    this.updateFormValue();
  }

  removeFrameFromGroup(gi: number, fi: number): void {
    const group = this.content.textFrameGroups[gi];
    if (!group || group.frames.length <= 1) return;
    group.frames.splice(fi, 1);
    this.rebuildGroupControls();
    this.updateFormValue();
  }

  // --- Fonts ---

  toggleFontInGroup(gi: number, fontFamily: string): void {
    const group = this.content.textFrameGroups[gi];
    if (!group) return;
    const idx = group.allowedFonts.indexOf(fontFamily);
    if (idx >= 0) {
      group.allowedFonts.splice(idx, 1);
      if (group.defaultFont === fontFamily) {
        const fallback = group.allowedFonts[0] || 'Roboto';
        group.defaultFont = fallback;
        this.groupControls[gi]?.defaultFont.setValue(fallback, { emitEvent: false });
      }
    } else {
      group.allowedFonts.push(fontFamily);
      if (!group.allowedFonts.includes(group.defaultFont)) {
        group.defaultFont = group.allowedFonts[0];
        this.groupControls[gi]?.defaultFont.setValue(group.defaultFont, { emitEvent: false });
      }
    }
    this.updateFormValue();
  }

  getEffectiveFonts(group: TextFrameGroupConfig): FontCatalogEntry[] {
    if (group.allowedFonts.length === 0) return this.fontCatalog;
    return this.fontCatalog.filter(f => group.allowedFonts.includes(f.family));
  }

  // --- Colors ---

  addColorToGroup(gi: number, color: string, colorName?: string): void {
    const group = this.content.textFrameGroups[gi];
    if (!group || !color) return;
    const hex = color.startsWith('#') ? color.toUpperCase() : `#${color.toUpperCase()}`;
    if (group.allowedColors.some(c => c.hex.toUpperCase() === hex)) return;
    group.allowedColors.push({ hex, name: colorName?.trim() || hex });
    this.updateFormValue();
  }

  removeColorFromGroup(gi: number, ci: number): void {
    const group = this.content.textFrameGroups[gi];
    if (!group) return;
    const removed = group.allowedColors.splice(ci, 1);
    if (removed[0]?.hex === group.defaultColor && group.allowedColors.length > 0) {
      group.defaultColor = group.allowedColors[0].hex;
      this.groupControls[gi]?.defaultColor.setValue(group.defaultColor, { emitEvent: false });
    }
    this.updateFormValue();
  }

  updateColorName(gi: number, ci: number, event: Event): void {
    const group = this.content.textFrameGroups[gi];
    if (!group?.allowedColors[ci]) return;
    const input = event.target as HTMLInputElement;
    group.allowedColors[ci].name = input.value.trim() || group.allowedColors[ci].hex;
    this.updateFormValue();
  }

  openAddColorModal(gi: number): void {
    this.addColorGroupIndex = gi;
    this.addColorHex = '#000000';
    this.addColorName = '';
    this.cdr.detectChanges();
  }

  cancelAddColor(): void {
    this.addColorGroupIndex = -1;
    this.cdr.detectChanges();
  }

  confirmAddColor(): void {
    if (this.addColorGroupIndex >= 0) {
      this.addColorToGroup(this.addColorGroupIndex, this.addColorHex, this.addColorName);
    }
    this.addColorGroupIndex = -1;
    this.cdr.detectChanges();
  }

  parseFontSizeOptions(gi: number, value: string): void {
    const group = this.content.textFrameGroups[gi];
    if (!group) return;
    group.fontSizeOptions = value.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n > 0);
    this.updateFormValue();
  }

  fontSizeOptionsString(group: TextFrameGroupConfig): string {
    return group.fontSizeOptions.join(', ');
  }
}
